import { absolutePosition } from "./auto-fuse";
import { getDefinition } from "./catalog";
import { effectiveDimensions } from "./effective";
import { endpointPos } from "./store-helpers";
import type { Cable, SystemNode } from "./types";

/** Cables sharing one component endpoint and heading roughly the same way.
 *  Direction only — polarity plays no part. */
export type CableBundle = {
  /** Routed first, and passed to every follower as a corridor to hug. */
  primaryId: string;
  followerIds: string[];
  sharedNodeId: string;
};

/** Max bearing gap between consecutive cables in one bundle. 45° keeps
 *  near-perpendicular cables apart — routing those in parallel distorts one. */
const MAX_BUNDLE_GAP = Math.PI / 4;

type Entry = { cableId: string; angle: number };

/** Single-link clustering by bearing. The first and last clusters merge across
 *  the ±π seam, or westward cables split in two. */
function clusterByAngle(entries: Entry[]): Entry[][] {
  if (entries.length === 0) return [];
  const sorted = [...entries].sort((a, b) => a.angle - b.angle);
  const clusters: Entry[][] = [[sorted[0]]];
  for (let i = 1; i < sorted.length; i++) {
    const gap = sorted[i].angle - sorted[i - 1].angle;
    if (gap > MAX_BUNDLE_GAP) {
      clusters.push([sorted[i]]);
    } else {
      clusters[clusters.length - 1].push(sorted[i]);
    }
  }
  if (clusters.length > 1) {
    const first = clusters[0];
    const last = clusters[clusters.length - 1];
    const wrapGap =
      2 * Math.PI - (last[last.length - 1].angle - first[0].angle);
    if (wrapGap <= MAX_BUNDLE_GAP) {
      last.push(...first);
      clusters.shift();
    }
  }
  return clusters;
}

/**
 * Only cables in `eligibleIds` count — a follower needs its primary routed in the
 * same pass to consume the corridor. Junction endpoints are skipped:
 * `planJunctionFanOut` owns their geometry.
 */
export function detectCableBundles(
  cables: Cable[],
  nodes: SystemNode[],
  eligibleIds: ReadonlySet<string>,
): CableBundle[] {
  const groups = new Map<string, Entry[]>();

  for (const cable of cables) {
    if (!eligibleIds.has(cable.id)) continue;
    for (const sharedNodeId of [cable.fromNodeId, cable.toNodeId]) {
      const sharedNode = nodes.find((n) => n.id === sharedNodeId);
      if (!sharedNode || sharedNode.kind !== "component") continue;
      const def = getDefinition(
        sharedNode.definitionId,
        sharedNode.customDefinition,
      );
      if (!def || def.renderVariant === "junction") continue;
      const farNodeId =
        cable.fromNodeId === sharedNodeId ? cable.toNodeId : cable.fromNodeId;
      const farTermId =
        cable.fromNodeId === sharedNodeId
          ? cable.toTerminalId
          : cable.fromTerminalId;
      // Bearing origin is the node's centre: from the top-left, a wide body skews
      // the bearing by up to ~45° and splits genuine same-direction bundles.
      const sharedAbs = absolutePosition(sharedNode, nodes);
      const dims = effectiveDimensions(def, sharedNode);
      const cx = sharedAbs.x + dims.width / 2;
      const cy = sharedAbs.y + dims.height / 2;
      const farPt = endpointPos(farNodeId, farTermId, nodes);
      if (!farPt) continue;
      const angle = Math.atan2(farPt.y - cy, farPt.x - cx);
      const list = groups.get(sharedNodeId) ?? [];
      list.push({ cableId: cable.id, angle });
      groups.set(sharedNodeId, list);
    }
  }

  const bundles: CableBundle[] = [];
  const usedCables = new Set<string>();
  const nodeIds = [...groups.keys()].sort();

  for (const nodeId of nodeIds) {
    const all = groups.get(nodeId)!;
    const free = all.filter((e) => !usedCables.has(e.cableId));
    if (free.length < 2) continue;
    const clusters = clusterByAngle(free);
    for (const cluster of clusters) {
      if (cluster.length < 2) continue;
      const ids = cluster.map((e) => e.cableId).sort();
      const [primaryId, ...followerIds] = ids;
      bundles.push({ primaryId, followerIds, sharedNodeId: nodeId });
      for (const id of ids) usedCables.add(id);
    }
  }

  return bundles;
}
